import { z } from 'zod';

import { emptyStringToHyphenSchema, emptyStringToNullSchema } from './utils';
import type { MapToX } from '../types';

export type LabwareProperties = {
  id: string;
  // TODO IMMEDIATELY: store {comments, showCommentInNodeGraph} elsewhere
  labwareType: LabwareType;
  labwareDefinitionURI: string | null;
  nickname: string;
  content: LabwareContent;
  /** custom label to describe what's inside the labware, if non-empty. */
  contentDescription: string | null;
  closureState: LabwareClosureState;
  /** Multi-use is used across batched, single-use is not. */
  singleOrMultiUse: SingleOrMultiUse;
  /** Ignored if singleOrMultiUse is "single" */
  totalUnits: number | null;
  maxIdleTimeExists: boolean;
  /** Ignored if maxIdleTimeExists is "false" */
  maxIdleTime: number | null;
  /** instrument ID */
  startLocation: string | null;
  /** string representing slot ranges, eg "1-10,15" */
  slot: string;
};

/** This is LabwareProperties made more generic for HTML form components.
 * String fields represent "no value" using empty string "" instead of null/undefined
 * to comply with MUI form components (eg Select).
 */
export type LabwarePropertiesForm = MapToX<
  Omit<
    LabwareProperties,
    'id' | 'totalUnits' | 'maxIdleTime' | 'maxIdleTimeExists'
  >,
  string
> & {
  maxIdleTimeExists: boolean;
  maxIdleTime: number | null;
  totalUnits: number | null;
};

export const allLabwareTypes = [
  'tiprack',
  'reservoir',
  'tuberack',
  'microplate',
] as const;
export const labwareTypeSchema = z.enum(allLabwareTypes);
export type LabwareType = z.infer<typeof labwareTypeSchema>;

export const labwareContentSchema = z.enum([
  'empty',
  'sample',
  'reagent',
  'other',
]);
export type LabwareContent = z.infer<typeof labwareContentSchema>;

export const isLabwareType = (value: unknown): value is LabwareType =>
  typeof value === 'string' && allLabwareTypes.includes(value as LabwareType);

// NOTE(IL, 2024-03-18): plates can be sealed/lidded; tubes can be capped/uncapped.
export const labwareClosureStateSchema = z.enum([
  'open',
  'sealed',
  'unsealed',
  'lidded',
  'capped',
  'uncapped',
]);
export type LabwareClosureState = z.infer<typeof labwareClosureStateSchema>;

export const singleOrMultiUseSchema = z.enum(['single', 'multi']);
export type SingleOrMultiUse = z.infer<typeof singleOrMultiUseSchema>;

const TRIM_SPACES_COMMAS_REGEX = /(^[|,\s]+)|(|,|\s)+$/g;

export const labwarePropertiesSchema = z.object({
  id: z.string(),
  labwareType: labwareTypeSchema,
  labwareDefinitionURI: emptyStringToNullSchema,
  nickname: emptyStringToHyphenSchema,
  content: labwareContentSchema,
  /** custom label to describe what's inside the labware, if non-empty. */
  contentDescription: emptyStringToNullSchema,
  closureState: labwareClosureStateSchema,
  /** Multi-use is used across batched, single-use is not. */
  singleOrMultiUse: singleOrMultiUseSchema,
  totalUnits: z
    .number()
    .nullable()
    .transform((value) => {
      // must be positive
      if (typeof value === 'number' && value > 0) {
        return value;
      }
      return null;
    }),
  maxIdleTimeExists: z.boolean(),
  maxIdleTime: z
    .number()
    .nullable()
    .transform((value) => {
      // must be positive
      if (typeof value === 'number' && value > 0) {
        return value;
      }
      return null;
    }),
  /** instrument ID */
  startLocation: emptyStringToNullSchema,
  /** string representing slot ranges, eg "1-10,15" */
  slot: z
    .string()
    .transform((value) => value.replace(TRIM_SPACES_COMMAS_REGEX, '')),
}) satisfies z.ZodType<LabwareProperties, z.ZodTypeDef, LabwarePropertiesForm>;

export function castLabwareField<Name extends keyof LabwarePropertiesForm>(
  values: LabwarePropertiesForm,
  name: Name
): z.SafeParseReturnType<LabwarePropertiesForm[Name], LabwareProperties[Name]> {
  // NOTE(IL, 2025-02-18): Zod/TS doesn't seem to be able to match up the typing like this,
  // but the return value should be guaranteed to match LabwareProperties[Name] if valid.
  const fieldSchema = labwarePropertiesSchema.shape[name];
  return fieldSchema.safeParse(values[name]) as z.SafeParseReturnType<
    LabwarePropertiesForm[Name],
    LabwareProperties[Name]
  >;
}

/** If casting fails, use the fallback, so you always have a result. */
export function castLabwareFieldWithFallback<
  Name extends keyof LabwarePropertiesForm
>(
  values: LabwarePropertiesForm,
  name: Name,
  fallback: LabwareProperties[Name]
): { castingFailed: boolean; value: LabwareProperties[Name] } {
  const maybeValue = castLabwareField(values, name);
  if (maybeValue.success)
    return { castingFailed: false, value: maybeValue.data };
  return { castingFailed: true, value: fallback };
}
